/**
 * ResumePreview component renders ResumeContent JSON as styled HTML.
 * Used in the preview panel to show real-time resume updates.
 *
 * Renders at full paper width (816px = 8.5") and scales down to fit container.
 * Supports multiple templates with different layouts:
 * - Modern: Single column, clean design
 * - Two-Column (pikachu): Sidebar with contact/skills, main area with experience
 * - Executive: Single column, formal design
 */

import { ResumeDateField } from '@/components/resume/resume-date-field'
import type { ResumeHighlights, TextSpan } from '@/lib/resume-diff'
import type { ArraySection } from '@/lib/resume-field-path'
import { cn } from '@/lib/utils'
import type { BuilderTemplate, ResumeContent, TemplateId } from '@/types'
import type { CSSProperties, ReactNode, RefCallback } from 'react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

/**
 * A single inline-editable text field (Phase 6). Editing is opt-in: the parent
 * renders this only when the preview is `editable`; otherwise the field is plain
 * text and this component is never mounted, so read-only consumers (agent
 * previews) are unchanged.
 *
 * The element is contentEditable and UNCONTROLLED while focused — React never
 * re-renders its text from state on a keystroke, which is what prevents caret
 * jumps. The DOM text is synced from `value` only while the field is NOT focused
 * (an AI turn or reload can then refresh it), and the edit is COMMITTED on blur
 * (or Enter for single-line fields). Escape reverts. No mount effect fires a
 * mutation — commits come only from user blur/Enter.
 */
function EditableText({
  value,
  onCommit,
  className,
  multiline = false,
  locked = false,
  ariaLabel,
  placeholder,
  autoFocus = false,
  ftueAnchor,
}: {
  value: string
  onCommit: (next: string) => void
  className?: string | undefined
  multiline?: boolean | undefined
  locked?: boolean | undefined
  ariaLabel?: string | undefined
  /** Muted hint shown while the field is empty (still click-through to focus). */
  placeholder?: string | undefined
  /** Place the caret here on mount (for a freshly revealed empty field). */
  autoFocus?: boolean | undefined
  ftueAnchor?: RefCallback<HTMLElement> | undefined
}) {
  const ref = useRef<HTMLSpanElement>(null)

  // Keep the DOM text in sync with `value` ONLY when this field isn't focused,
  // so an external update (AI turn / reload) refreshes it without clobbering the
  // caret mid-edit. Setting textContent imperatively (rather than as React
  // children) is required for a contentEditable element React doesn't manage.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    if (document.activeElement === el) return
    if (el.textContent !== value) el.textContent = value
  }, [value])

  // biome-ignore lint/correctness/useExhaustiveDependencies: focus once on mount
  useEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [])

  const commit = () => {
    const el = ref.current
    if (!el) return
    const next = (el.textContent ?? '').trim()
    if (next !== value) onCommit(next)
  }

  return (
    <span
      ref={(element) => {
        ref.current = element
        ftueAnchor?.(element)
      }}
      role="textbox"
      aria-label={ariaLabel}
      tabIndex={0}
      contentEditable
      suppressContentEditableWarning
      // The placeholder is a CSS ::before on the empty element (reserves inline
      // layout, unlike an absolute overlay, so it never collides with the next
      // section). It clears on the first keystroke and returns when emptied.
      data-placeholder={placeholder}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !multiline) {
          e.preventDefault()
          e.currentTarget.blur()
        } else if (e.key === 'Escape') {
          const el = ref.current
          if (el) el.textContent = value // revert to the last committed value
          e.currentTarget.blur()
        }
      }}
      className={cn(
        'cursor-text rounded-sm outline-none focus:bg-blue-50 focus:ring-1 focus:ring-blue-300',
        placeholder &&
          'empty:before:text-slate-400 empty:before:italic empty:before:content-[attr(data-placeholder)]',
        // Subtle provenance marker: a dashed underline on fields the user edited.
        locked && 'border-b border-dashed border-amber-400/70',
        className,
      )}
    />
  )
}

function WordSpans({ spans }: { spans: TextSpan[] }) {
  // Group consecutive spans with the same highlight state into phrases
  const groups: { highlight: boolean; words: string[] }[] = []
  for (const span of spans) {
    const last = groups[groups.length - 1]
    if (last && last.highlight === span.highlight) {
      last.words.push(span.text)
    } else {
      groups.push({ highlight: span.highlight, words: [span.text] })
    }
  }

  return (
    <>
      {groups.map((group, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static render order, no reorder
        <span key={i}>
          {i > 0 && ' '}
          {group.highlight ? (
            <mark className="bg-blue-100 text-inherit rounded-sm px-0.5 not-italic">
              {group.words.join(' ')}
            </mark>
          ) : (
            group.words.join(' ')
          )}
        </span>
      ))}
    </>
  )
}

// Standard letter paper width in pixels (8.5" at 96dpi)
const PAPER_WIDTH = 816

interface ResumePreviewProps {
  content: ResumeContent
  template?: TemplateId | BuilderTemplate
  className?: string
  /** Justify body copy (summary, bullets, descriptions) — used in agent document previews. */
  justifyBody?: boolean
  /** When provided, highlights added/reworded lines in blue. */
  highlights?: ResumeHighlights
  /**
   * Collapse the container to the *scaled* content height. The paper is scaled
   * with a CSS transform, which does not shrink the reserved layout box, so by
   * default the container reserves the unscaled height (leaving whitespace below
   * a scaled preview). When true, the container height tracks the visible scaled
   * height — needed so a fixed page frame can scroll to exactly one page of
   * content. Off by default to preserve existing callers.
   */
  fitHeight?: boolean
  /**
   * Opt-in inline editing (Phase 6). Off by default so existing consumers (agent
   * previews) stay render-only. When true AND onFieldCommit is provided, text
   * fields become click-to-edit; each commit reports its field path + new value.
   */
  editable?: boolean
  /** Called on blur/Enter with the edited field's path and its new value. */
  onFieldCommit?: (path: string, value: string) => void
  /** Field paths the user has already edited — shown with a provenance marker. */
  lockedFields?: string[]
  /**
   * Opt-in: when the content is still empty, render a labeled placeholder resume
   * instead of a blank page (builder's fresh / from-scratch draft). Off by
   * default so render-only consumers (agent previews) are unchanged.
   */
  showEmptyState?: boolean
  /**
   * Builder-only inline section editing (wired alongside onFieldCommit):
   * - onAddEntry: append to an array section — skills take a value, experience/
   *   education append a blank row the user fills field-by-field.
   * - onRemoveEntry: drop a row (e.g. a blank one added by mistake).
   * - onAddBullet: append a blank achievement bullet to an experience entry.
   */
  onAddEntry?: (section: ArraySection, value?: string) => void
  onRemoveEntry?: (section: ArraySection, index: number) => void
  onAddBullet?: (expIndex: number) => void
  /** Focused builder FTUE anchors; omitted by read-only preview consumers. */
  editFieldFtueAnchor?: RefCallback<HTMLElement>
  /**
   * Override the template's built-in accent color (Export panel). Undefined
   * (default) keeps every existing caller's behavior unchanged — the
   * template's own `templateThemes` color applies.
   */
  accentColor?: string
  /**
   * Per-element pt font-size overrides (Export panel font size controls) —
   * mirrors resume_templates.FontSizeOverrides' 4 categories and which
   * selector each drives server-side: name -> the name heading,
   * sectionHeader -> each section's h2, subHeader -> the job-title/degree/
   * project-name line, body -> the Summary paragraph only (matches the
   * backend: achievements/dates/company/etc. keep their own fixed size
   * regardless of body_size there too). All undefined (default) keeps every
   * existing caller's fixed Tailwind text sizing unchanged.
   */
  nameSize?: number
  sectionHeaderSize?: number
  subHeaderSize?: number
  bodySize?: number
}

/** `{n}pt` inline style, or undefined so the element's own Tailwind text-size
 * class applies unchanged — mirrors resume_templates.py's `{n}pt` CSS. */
function ptStyle(size: number | undefined): CSSProperties | undefined {
  return size ? { fontSize: `${size}pt` } : undefined
}

// Template-specific styles
const templateStyles = {
  modern: {
    container: 'font-sans',
    header: 'pb-5 mb-6',
    sidebar: '',
    sidebarHeader: '',
    sidebarSection: '',
    sidebarTitle: '',
    sidebarText: '',
    sidebarContactText: '',
    name: 'text-2xl font-bold text-[var(--resume-ink)]',
    contactInfo: 'flex flex-wrap items-center gap-x-0 gap-y-1 text-sm text-gray-500 mt-2',
    section: 'mb-5',
    sectionTitle:
      'text-xs font-bold text-[var(--resume-ink)] uppercase tracking-widest border-b-2 border-[var(--resume-accent)] pb-1 mb-3',
    jobTitle: 'font-semibold text-gray-900',
    company: 'text-[var(--resume-accent)]',
    dates: 'text-sm text-gray-400',
    bullet: 'text-gray-600 ml-4 list-disc',
    link: 'text-[var(--resume-accent)] hover:underline',
    skillBg:
      'bg-[var(--resume-soft)] text-[var(--resume-accent-dark)] border border-[var(--resume-border)]',
  },
  pikachu: {
    container: 'font-sans',
    header: 'bg-[var(--resume-accent)] text-white p-5 -mx-8 -mt-8 mb-5',
    // Real two-column layout (PEL-1024): matches the backend's PIKACHU_TEMPLATE
    // CSS (.sidebar/.sidebar-header/.sidebar-section/.sidebar-title/simple-list) --
    // a light-gray sidebar column with a dark accent contact-info block on top.
    sidebar: 'w-[230px] shrink-0 bg-[#F3F4F6]',
    sidebarHeader: 'bg-[var(--resume-accent-dark)] px-5 py-6 text-white',
    sidebarSection: 'border-b border-gray-200 px-5 py-4 last:border-b-0',
    sidebarTitle:
      'mb-2.5 text-[9px] font-bold uppercase tracking-wide text-[var(--resume-accent-dark)]',
    sidebarText: 'text-[9px] text-gray-600',
    // Contact list sits in sidebarHeader (dark accent bg), not the light
    // sidebar body sidebarText is otherwise tuned for -- was reusing
    // sidebarText's gray-600, nearly invisible on that dark background.
    // rgba(255,255,255,0.9) matches the backend's PIKACHU_TEMPLATE
    // `.contact-list li` color (PEL-1024 follow-up).
    sidebarContactText: 'text-[9px] text-white/90',
    name: 'text-2xl font-bold text-white mb-2',
    contactInfo: 'flex flex-wrap gap-4 text-sm text-blue-100',
    section: 'mb-4',
    sectionTitle:
      'text-xs font-bold text-[var(--resume-accent)] uppercase tracking-widest border-b-2 border-[var(--resume-accent)] pb-1 mb-3',
    jobTitle: 'font-semibold text-gray-900',
    company: 'text-[var(--resume-accent)]',
    dates: 'text-sm text-gray-400',
    bullet: 'text-gray-600 ml-4 list-disc',
    link: 'text-[var(--resume-accent)] hover:underline',
    skillBg: 'bg-gray-100 border border-gray-200 text-gray-600',
  },
  // executive was the one template with zero accent-color participation at
  // all (fully hardcoded slate, no var() anywhere) - now uses the same 4 CSS
  // vars as modern for header border/company/link/skillBg, keeping name and
  // section-title in --resume-ink (not accent) so the conservative identity
  // holds by default while still responding when the accent picker changes.
  // 2026-09-08, in response to "accent color should affect every template."
  executive: {
    container: 'font-serif',
    header: 'text-center border-b-2 border-[var(--resume-accent-dark)] pb-5 mb-6',
    sidebar: '',
    sidebarHeader: '',
    sidebarSection: '',
    sidebarTitle: '',
    sidebarText: '',
    sidebarContactText: '',
    name: 'text-3xl font-bold text-[var(--resume-ink)] tracking-wide',
    contactInfo: 'flex flex-wrap justify-center gap-x-0 gap-y-1 text-sm text-gray-500 mt-3',
    section: 'mb-5',
    sectionTitle:
      'text-xs font-bold text-[var(--resume-ink)] uppercase tracking-widest border-b border-[var(--resume-border)] pb-1 mb-3',
    jobTitle: 'font-bold text-[var(--resume-ink)]',
    company: 'text-[var(--resume-accent)]',
    dates: 'text-sm text-gray-400 italic',
    bullet: 'text-gray-600 ml-4 list-disc',
    link: 'text-[var(--resume-accent-dark)] hover:underline',
    skillBg:
      'bg-[var(--resume-soft)] text-[var(--resume-accent-dark)] border border-[var(--resume-border)]',
  },
  // The 5 templates below are 2026-09-08's Export template expansion (see
  // resume-detail-overhaul dev/todo.md for the archetype rationale). Each
  // gets its own full style entry (not just a recolor of an existing one) -
  // `styles` is looked up by the exact template now, not the collapsed
  // `templateLayout` bucket, so these can carry their own identity while
  // still reusing one of the 3 structural JSX skeletons below (see
  // templateLayout derivation) - mirrors the backend's own pattern of
  // reusing an existing HTML builder with a new CSS skin only.
  two_column_photo: {
    container: 'font-sans',
    header: 'bg-[var(--resume-ink)] text-white p-6 -mx-8 -mt-8 mb-5 text-center',
    // Whole sidebar is dark here (backend: `.sidebar { background: primary_dark }`) --
    // name/headline live at its top (nameInSidebar), not a separate main header.
    sidebar: 'w-[240px] shrink-0 bg-[var(--resume-accent-dark)] text-white',
    sidebarHeader: 'px-6 py-7 text-center text-white',
    sidebarSection: 'border-t border-white/15 px-6 py-3.5',
    sidebarTitle: 'mb-2.5 text-[8.5px] font-bold uppercase tracking-wide text-white/90',
    sidebarText: 'text-[8.5px] text-white/85',
    // Whole sidebar is dark here already, so this is the same as sidebarText
    // -- kept as its own key for consistency with pikachu, where the two
    // differ (contact list sits in a dark header, sidebarText's body doesn't).
    sidebarContactText: 'text-[8.5px] text-white/85',
    name: 'text-2xl font-bold text-white mb-1',
    contactInfo: 'flex flex-wrap justify-center gap-4 text-sm text-slate-300',
    section: 'mb-4',
    sectionTitle:
      'text-xs font-bold text-[var(--resume-accent)] uppercase tracking-widest border-b-2 border-[var(--resume-accent)] pb-1 mb-3',
    jobTitle: 'font-semibold text-gray-900',
    company: 'text-[var(--resume-accent)]',
    dates: 'text-sm text-gray-400',
    bullet: 'text-gray-600 ml-4 list-disc',
    link: 'text-[var(--resume-accent)] hover:underline',
    skillBg:
      'bg-[var(--resume-soft)] border border-[var(--resume-border)] text-[var(--resume-accent-dark)]',
  },
  bold_creative: {
    container: 'font-sans',
    header: 'bg-[var(--resume-accent)] text-white rounded-2xl p-6 mb-5',
    sidebar: '',
    sidebarHeader: '',
    sidebarSection: '',
    sidebarTitle: '',
    sidebarText: '',
    sidebarContactText: '',
    name: 'text-3xl font-extrabold text-white mb-2',
    contactInfo: 'flex flex-wrap gap-4 text-sm text-blue-100',
    section: 'mb-5',
    sectionTitle:
      'inline-block text-xs font-bold text-white bg-[var(--resume-accent-dark)] rounded-md px-3 py-1 mb-3',
    jobTitle: 'font-bold text-gray-900',
    company: 'text-[var(--resume-accent)] font-semibold',
    dates: 'text-sm text-gray-400',
    bullet: 'text-gray-600 ml-4 list-disc',
    link: 'text-white underline',
    skillBg: 'bg-[var(--resume-accent)] text-white border-none rounded-full px-3',
  },
  // Was fully hardcoded slate (deliberately grayscale by design) - now uses
  // the vars like executive does, name/section-title stay in --resume-ink so
  // the formal-CV identity holds by default (academic_cv's theme below is
  // itself slate-toned), while company/link/skillBg pick up the accent so
  // the picker has a visible effect. 2026-09-08, same accent-color pass.
  academic_cv: {
    container: 'font-serif text-[13px]',
    header: 'text-left border-b border-[var(--resume-accent-dark)] pb-3 mb-4',
    sidebar: '',
    sidebarHeader: '',
    sidebarSection: '',
    sidebarTitle: '',
    sidebarText: '',
    sidebarContactText: '',
    name: 'text-xl font-bold text-[var(--resume-ink)] tracking-wide',
    contactInfo: 'flex flex-wrap gap-x-0 gap-y-1 text-xs text-gray-500 mt-2',
    section: 'mb-3',
    sectionTitle:
      'text-[10px] font-bold text-[var(--resume-ink)] uppercase tracking-widest border-b border-[var(--resume-border)] pb-1 mb-2',
    jobTitle: 'font-bold text-[var(--resume-ink)] text-sm',
    company: 'text-[var(--resume-accent)] text-sm',
    dates: 'text-xs text-gray-400 italic',
    bullet: 'text-gray-600 ml-4 list-disc text-xs',
    link: 'text-[var(--resume-accent)] hover:underline',
    skillBg:
      'bg-[var(--resume-soft)] text-[var(--resume-accent)] border border-[var(--resume-border)]',
  },
  timeline: {
    container: 'font-sans',
    header: 'pb-5 mb-6 border-b-2 border-[var(--resume-accent)]',
    sidebar: '',
    sidebarHeader: '',
    sidebarSection: '',
    sidebarTitle: '',
    sidebarText: '',
    sidebarContactText: '',
    name: 'text-2xl font-bold text-[var(--resume-ink)]',
    contactInfo: 'flex flex-wrap items-center gap-x-0 gap-y-1 text-sm text-gray-500 mt-2',
    section: 'mb-5',
    sectionTitle:
      'text-xs font-bold text-[var(--resume-accent)] uppercase tracking-widest border-b border-gray-200 pb-1 mb-3',
    jobTitle: 'font-semibold text-gray-900',
    company: 'text-[var(--resume-accent)]',
    dates: 'text-sm text-gray-400',
    bullet: 'text-gray-600 ml-4 list-disc marker:text-[var(--resume-accent)]',
    link: 'text-[var(--resume-accent)] hover:underline',
    skillBg:
      'bg-[var(--resume-soft)] text-[var(--resume-accent-dark)] border border-[var(--resume-border)]',
  },
}

const templateThemes: Record<BuilderTemplate | TemplateId, Record<string, string>> = {
  modern: {
    '--resume-accent': '#2563eb',
    '--resume-accent-dark': '#1d4ed8',
    '--resume-ink': '#0f172a',
    '--resume-soft': '#eff6ff',
    '--resume-border': '#bfdbfe',
  },
  executive: {
    '--resume-accent': '#475569',
    '--resume-accent-dark': '#334155',
    '--resume-ink': '#1e293b',
    '--resume-soft': '#f1f5f9',
    '--resume-border': '#cbd5e1',
  },
  pikachu: {
    '--resume-accent': '#2563eb',
    '--resume-accent-dark': '#1d4ed8',
    '--resume-ink': '#0f172a',
    '--resume-soft': '#eff6ff',
    '--resume-border': '#bfdbfe',
  },
  // These 4 mostly hardcode their own colors above rather than using the CSS
  // vars (matching pikachu's existing convention, since they reuse pikachu's
  // or modern's structural JSX) - entries still required so every template
  // has a complete theme object (type is a Record over the full union) and
  // so accentColor override + var()-based classes (bold_creative, timeline)
  // resolve to something sane by default.
  two_column_photo: {
    '--resume-accent': '#2563eb',
    '--resume-accent-dark': '#1d4ed8',
    '--resume-ink': '#0f172a',
    '--resume-soft': '#eff6ff',
    '--resume-border': '#bfdbfe',
  },
  bold_creative: {
    '--resume-accent': '#2563eb',
    '--resume-accent-dark': '#1d4ed8',
    '--resume-ink': '#0f172a',
    '--resume-soft': '#eff6ff',
    '--resume-border': '#bfdbfe',
  },
  academic_cv: {
    '--resume-accent': '#334155',
    '--resume-accent-dark': '#1e293b',
    '--resume-ink': '#0f172a',
    '--resume-soft': '#f8fafc',
    '--resume-border': '#cbd5e1',
  },
  timeline: {
    '--resume-accent': '#2563eb',
    '--resume-accent-dark': '#1d4ed8',
    '--resume-ink': '#0f172a',
    '--resume-soft': '#eff6ff',
    '--resume-border': '#bfdbfe',
  },
}

/**
 * Derive the accent/accent-dark/soft/border CSS vars from a single override
 * color (Resume Detail's Export panel accent picker) — mirrors the shape of
 * templateThemes' per-template palette without needing 4 separate pickers.
 */
function accentOverrideTheme(hex: string): Record<string, string> {
  const clean = hex.replace('#', '')
  const r = Number.parseInt(clean.slice(0, 2), 16) || 0
  const g = Number.parseInt(clean.slice(2, 4), 16) || 0
  const b = Number.parseInt(clean.slice(4, 6), 16) || 0
  const mix = (channel: number, target: number, amount: number) =>
    Math.round(channel + (target - channel) * amount)
  const toHex = (n: number) => n.toString(16).padStart(2, '0')
  const dark = `#${toHex(mix(r, 0, 0.25))}${toHex(mix(g, 0, 0.25))}${toHex(mix(b, 0, 0.25))}`
  const soft = `#${toHex(mix(r, 255, 0.92))}${toHex(mix(g, 255, 0.92))}${toHex(mix(b, 255, 0.92))}`
  const border = `#${toHex(mix(r, 255, 0.7))}${toHex(mix(g, 255, 0.7))}${toHex(mix(b, 255, 0.7))}`
  return {
    '--resume-accent': hex,
    '--resume-accent-dark': dark,
    '--resume-soft': soft,
    '--resume-border': border,
  }
}

/** True when a draft has no meaningful content yet (fresh / from-scratch). */
function isResumeEmpty(content: ResumeContent): boolean {
  const c = content.contact
  return (
    !(c?.name ?? '').trim() &&
    !(c?.email ?? '').trim() &&
    !(content.summary ?? '').trim() &&
    !content.experience?.length &&
    !content.education?.length &&
    !content.skills?.length &&
    !content.certifications?.length &&
    !content.projects?.length &&
    !content.languages?.length
  )
}

/**
 * Placeholder shown (opt-in) while a draft is still empty, so a fresh or
 * from-scratch resume never renders as a blank page. Purely decorative
 * (aria-hidden): it names the sections the chat will fill in, with muted
 * skeleton lines for shape. Replaced by the real resume as soon as content
 * arrives.
 */
function EmptyResumePlaceholder({ styles }: { styles: (typeof templateStyles)['modern'] }) {
  const sections = [
    { title: 'Summary', hint: 'Your professional summary will appear here as you chat.' },
    { title: 'Experience', hint: 'Your work experience will appear here.' },
    { title: 'Education', hint: 'Your education will appear here.' },
    { title: 'Skills', hint: 'Your skills will appear here.' },
  ]
  return (
    <div aria-hidden className="select-none">
      <div className={styles.header}>
        <div className="h-7 w-56 max-w-full rounded bg-slate-200" />
        <div className="mt-3 h-3 w-72 max-w-full rounded bg-slate-100" />
      </div>
      {sections.map((s) => (
        <section key={s.title} className={styles.section}>
          <h2 className={styles.sectionTitle}>{s.title}</h2>
          <p className="text-sm italic text-slate-400">{s.hint}</p>
          <div className="mt-2 space-y-1.5">
            <div className="h-2.5 w-full rounded bg-slate-100" />
            <div className="h-2.5 w-11/12 rounded bg-slate-100" />
            <div className="h-2.5 w-4/5 rounded bg-slate-100" />
          </div>
        </section>
      ))}
    </div>
  )
}

/**
 * A single muted section hint shown (in builder mode) in place of a section that
 * has no content yet, so a partially-filled draft still guides the user to the
 * remaining sections instead of them silently disappearing once the first edit
 * lands. aria-hidden — purely a build-time affordance, never part of the resume.
 */
function SectionHintStub({
  styles,
  title,
  hint,
}: {
  styles: (typeof templateStyles)['modern']
  title: string
  hint: string
}) {
  return (
    <section aria-hidden className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <p className="text-sm italic text-slate-400">{hint}</p>
    </section>
  )
}

/**
 * Inline "add a skill" chip (builder only). Type a skill and press Enter (or
 * blur) to append it; empty input adds nothing, so the placeholder simply
 * returns. Kept uncontrolled with a remount key so it clears after each add.
 */
function AddSkillChip({
  styles,
  onAdd,
}: {
  styles: (typeof templateStyles)['modern']
  onAdd: (value: string) => void
}) {
  const [key, setKey] = useState(0)
  return (
    <span
      className={cn('px-2.5 py-1 rounded-md text-xs font-medium', styles.skillBg, 'opacity-80')}
    >
      <EditableText
        key={key}
        value=""
        ariaLabel="add-skill"
        placeholder="+ add a skill"
        onCommit={(v) => {
          const skill = v.trim()
          if (!skill) return
          onAdd(skill)
          setKey((k) => k + 1) // remount to reset the input for the next skill
        }}
      />
    </span>
  )
}

export function ResumePreview({
  content,
  template = 'modern',
  className,
  justifyBody = false,
  highlights,
  fitHeight = false,
  editable = false,
  onFieldCommit,
  lockedFields,
  showEmptyState = false,
  onAddEntry,
  onRemoveEntry,
  onAddBullet,
  editFieldFtueAnchor,
  accentColor,
  nameSize,
  sectionHeaderSize,
  subHeaderSize,
  bodySize,
}: ResumePreviewProps) {
  // templateLayout picks which of the 3 structural JSX skeletons below to use
  // (field ordering/separators - e.g. pikachu's stacked title/company vs.
  // modern's "at" separator); `styles` (colors/classes) is looked up by the
  // *exact* template so each of the 4 new templates keeps its own visual
  // identity while still reusing whichever skeleton its backend builder
  // structurally matches (two_column_photo reuses _build_pikachu_main
  // content-wise, academic_cv reads like executive's comma-separated line,
  // bold_creative/timeline read like modern's).
  const templateLayout =
    template === 'executive' || template === 'academic_cv'
      ? 'executive'
      : template === 'pikachu' || template === 'two_column_photo'
        ? 'pikachu'
        : 'modern'
  // PEL-1024: pikachu/two_column_photo are genuinely two-column server-side
  // (sidebar with contact/skills/certs/languages, colored header only over
  // the main column) -- this preview used to fake them as a single top-down
  // column, so the download looked nothing like what was shown here.
  // two_column_photo additionally folds name/headline into the sidebar
  // itself instead of a separate main-column header (see
  // DocumentRendererService._build_photo_two_column_sidebar).
  const isSidebarLayout = templateLayout === 'pikachu'
  const nameInSidebar = template === 'two_column_photo'
  const styles = templateStyles[template]
  const bodyTextClass = cn('text-gray-700', justifyBody && 'text-justify')
  const hl = highlights
  const containerRef = useRef<HTMLDivElement>(null)

  // Editing is live only when opted in AND a commit sink is wired.
  const canEdit = editable && typeof onFieldCommit === 'function'
  const lockedSet = useMemo(() => new Set(lockedFields ?? []), [lockedFields])
  const emptyBulletPath = useMemo(() => {
    for (const [expIndex, experience] of (content.experience ?? []).entries()) {
      const bulletIndex = experience.achievements?.findIndex((bullet) => !bullet.trim()) ?? -1
      if (bulletIndex >= 0) return `experience.${expIndex}.achievements.${bulletIndex}`
    }
    return null
  }, [content.experience])

  /**
   * Render one text field. When editing is off, returns the read-only node
   * (`read`, defaulting to the raw value) — identical to prior behavior, so diff
   * highlights and existing consumers are untouched. When editing is on, returns
   * an EditableText for that field path. Editable fields intentionally show plain
   * text (no diff <mark>): a caret can't live reliably inside React-managed marks.
   */
  const field = (
    path: string,
    value: string,
    opts?: {
      multiline?: boolean
      read?: ReactNode
      className?: string
      ariaLabel?: string
      placeholder?: string
      autoFocus?: boolean
      ftueAnchor?: RefCallback<HTMLElement>
    },
  ): ReactNode => {
    if (!canEdit) return opts?.read ?? value
    return (
      <EditableText
        value={value}
        multiline={opts?.multiline}
        locked={lockedSet.has(path)}
        className={opts?.className}
        ariaLabel={opts?.ariaLabel ?? path}
        placeholder={opts?.placeholder}
        autoFocus={opts?.autoFocus}
        ftueAnchor={opts?.ftueAnchor ?? (path === 'summary' ? editFieldFtueAnchor : undefined)}
        onCommit={(next) => onFieldCommit?.(path, next)}
      />
    )
  }
  /**
   * Render one date field (experience start/end, education graduation). Read-only
   * mode is the raw value, exactly as before. When editing is on, the value is
   * SET from a month/year picker rather than typed, so a manual edit can't leave
   * the draft holding an unreadable date (PEL-847).
   */
  const dateField = (
    path: string,
    value: string,
    opts?: { placeholder?: string; allowPresent?: boolean },
  ): ReactNode => {
    if (!canEdit) return value
    return (
      <ResumeDateField
        value={value}
        ariaLabel={path}
        locked={lockedSet.has(path)}
        placeholder={opts?.placeholder}
        allowPresent={opts?.allowPresent}
        onCommit={(next) => onFieldCommit?.(path, next)}
      />
    )
  }
  // Empty sections become directly editable only in the builder (showEmptyState
  // is off for read-only agent previews). Requires a commit sink (canEdit).
  const builderEditable = showEmptyState && canEdit
  // Placeholder opts for a field, applied only in the builder (so a blank
  // experience/education row shows "Job title", "Company", … to type into).
  const ph = (text: string) => (builderEditable ? { placeholder: text } : {})
  // A small round "remove this row" control shown next to a builder entry.
  const removeButton = (section: ArraySection, index: number) =>
    builderEditable && onRemoveEntry ? (
      <button
        type="button"
        aria-label={`Remove ${section} entry ${index + 1}`}
        onClick={() => onRemoveEntry(section, index)}
        className="ml-2 shrink-0 rounded-full px-1.5 text-xs text-slate-300 hover:bg-red-50 hover:text-red-600"
      >
        ✕
      </button>
    ) : null
  // A dashed "add a row" button shown under a builder section.
  const addRowButton = (
    label: string,
    onClick: () => void,
    ftueAnchor?: RefCallback<HTMLElement>,
  ) => (
    <button
      ref={ftueAnchor}
      type="button"
      onClick={onClick}
      className="mt-2 rounded-md border border-dashed border-slate-300 px-2 py-1 text-xs text-slate-500 hover:border-blue-400 hover:text-blue-600"
    >
      {label}
    </button>
  )
  const paperRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  // Scaled paper height, tracked only when fitHeight is set (see prop docs).
  const [scaledHeight, setScaledHeight] = useState<number | null>(null)

  // Calculate scale factor based on container width. A ResizeObserver (not just a
  // window-resize listener) is required so the scale corrects when the container
  // itself resizes after mount — e.g. rendered inside a dialog that animates open,
  // where the initial width is ~0 and would otherwise lock the scale in tiny.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const updateScale = () => {
      const containerWidth = container.offsetWidth
      // Ignore a zero-width measurement (pre-layout) so we don't lock in scale 0.
      if (containerWidth > 0) {
        setScale(Math.min(1, containerWidth / PAPER_WIDTH))
      }
    }
    updateScale()
    const observer = new ResizeObserver(updateScale)
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  // When fitting height, collapse the container to the scaled paper height so a
  // scroll frame reveals exactly the rendered content (not the unscaled box).
  // A ResizeObserver keeps it correct as content/template/scale change.
  useEffect(() => {
    if (!fitHeight) return
    const paper = paperRef.current
    if (!paper) return
    const measure = () => setScaledHeight(paper.offsetHeight * scale)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(paper)
    return () => observer.disconnect()
  }, [fitHeight, scale])

  // PEL-1024: contact items are shared between the flat header's horizontal
  // row and the sidebar layouts' vertical contact list.
  const contactItems = (
    [
      ['contact.email', content.contact.email],
      ['contact.phone', content.contact.phone],
      ['contact.location', content.contact.location],
      ['contact.linkedin', content.contact.linkedin],
      ['contact.website', content.contact.website],
    ] as const
  ).filter(([, value]) => Boolean(value)) as [string, string][]
  const renderContactValue = (path: string, item: string, linkClassName = styles.link) =>
    canEdit ? (
      // Editable: the raw value (so the link/email itself is edited).
      field(path, item)
    ) : item.startsWith('http') || item.startsWith('linkedin') ? (
      <a
        href={item.startsWith('http') ? item : `https://${item}`}
        className={linkClassName}
        target="_blank"
        rel="noopener noreferrer"
      >
        {item.startsWith('http') ? 'Website' : 'LinkedIn'}
      </a>
    ) : (
      <span>{item}</span>
    )
  const contactSep = templateLayout === 'executive' ? '|' : '·'
  const sidebarContactList = (
    <ul className="space-y-1.5">
      {contactItems.map(([path, item]) => (
        <li key={path} className={cn('flex items-center gap-1.5', styles.sidebarContactText)}>
          {renderContactValue(path, item, cn(styles.sidebarContactText, 'underline'))}
        </li>
      ))}
    </ul>
  )
  const nameField = (
    <h1 className={styles.name} style={ptStyle(nameSize)}>
      {field('contact.name', content.contact.name, {
        ...(builderEditable ? { placeholder: 'Your name' } : {}),
      })}
    </h1>
  )
  // First sentence of the summary, truncated -- the pikachu-family templates
  // show this as a tagline under the name (backend derives the same thing in
  // _build_pikachu_header / _build_photo_two_column_sidebar). Read-only: it's
  // derived from summary, not an editable field of its own.
  const summaryHeadline = (() => {
    if (!content.summary) return null
    const firstSentence = content.summary.split('.')[0] ?? ''
    return firstSentence.length > 80 ? `${firstSentence.slice(0, 77)}...` : firstSentence
  })()
  // Sidebar layouts (pikachu/two_column_photo) split contact info out of
  // the header row entirely into sidebarContactList
  // above; the main-column header (unused for two_column_photo, whose
  // name lives in the sidebar instead -- see sidebarHeaderBlock) just
  // gets the name plus the derived headline.
  const mainHeaderBlock =
    isSidebarLayout && !nameInSidebar ? (
      <div className={styles.header}>
        {nameField}
        {summaryHeadline && <p className="mt-1 text-xs opacity-90">{summaryHeadline}</p>}
      </div>
    ) : null
  const sidebarHeaderBlock = (
    <div className={styles.sidebarHeader}>
      {nameInSidebar && (
        <h1 className={cn(styles.name, 'mb-3 text-center')} style={ptStyle(nameSize)}>
          {field('contact.name', content.contact.name, {
            ...(builderEditable ? { placeholder: 'Your name' } : {}),
          })}
        </h1>
      )}
      {sidebarContactList}
    </div>
  )
  const skillsSectionSidebar =
    (content.skills && content.skills.length > 0) || builderEditable ? (
      <div className={styles.sidebarSection}>
        <div className={styles.sidebarTitle}>Skills</div>
        <div className="flex flex-wrap gap-1">
          {content.skills?.map((skill, skillIdx) => (
            <span
              key={skill}
              className={cn(
                'rounded px-1.5 py-0.5 text-[9px] font-medium',
                hl?.skills[skillIdx] && !canEdit
                  ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-300'
                  : styles.skillBg,
              )}
            >
              {field(`skills.${skillIdx}`, skill)}
            </span>
          ))}
          {builderEditable && onAddEntry && (
            <AddSkillChip styles={styles} onAdd={(v) => onAddEntry('skills', v)} />
          )}
        </div>
      </div>
    ) : null
  const certificationsSectionSidebar =
    content.certifications && content.certifications.length > 0 ? (
      <div className={styles.sidebarSection}>
        <div className={styles.sidebarTitle}>Certifications</div>
        <ul className="space-y-1">
          {content.certifications.map((cert, certIdx) => (
            <li key={cert} className={styles.sidebarText}>
              {field(`certifications.${certIdx}`, cert)}
            </li>
          ))}
        </ul>
      </div>
    ) : null
  const languagesSectionSidebar =
    content.languages && content.languages.length > 0 ? (
      <div className={styles.sidebarSection}>
        <div className={styles.sidebarTitle}>Languages</div>
        <p className={styles.sidebarText}>{content.languages.join(', ')}</p>
      </div>
    ) : null
  const sidebarBlock = (
    <aside className={styles.sidebar}>
      {sidebarHeaderBlock}
      {skillsSectionSidebar}
      {certificationsSectionSidebar}
      {languagesSectionSidebar}
    </aside>
  )

  return (
    <div
      ref={containerRef}
      className={cn('overflow-hidden', className)}
      style={fitHeight && scaledHeight != null ? { height: scaledHeight } : undefined}
    >
      <div
        ref={paperRef}
        style={{
          width: PAPER_WIDTH,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          ...(templateThemes[template] as CSSProperties),
          ...(accentColor ? (accentOverrideTheme(accentColor) as CSSProperties) : {}),
        }}
        className={cn('bg-white p-8 text-sm', styles.container)}
      >
        {showEmptyState && isResumeEmpty(content) && !builderEditable ? (
          // Read-only empty preview (no commit sink): decorative placeholder.
          // When builderEditable, fall through to the body so each empty section
          // renders its own inline-editable field / add affordance instead.
          <EmptyResumePlaceholder styles={styles} />
        ) : (
          (() => {
            // Summary/Experience/Education/Projects are identical content
            // whether the template lays out flat or two-column -- only WHERE
            // they render (single column vs. the sidebar layout's main
            // column) differs, so they're computed once and placed by both
            // branches below instead of duplicating this JSX per layout.
            const summarySection =
              content.summary || builderEditable ? (
                <section className={styles.section}>
                  <h2 className={styles.sectionTitle} style={ptStyle(sectionHeaderSize)}>
                    {templateLayout === 'executive' ? 'Professional Summary' : 'Summary'}
                  </h2>
                  <p
                    className={cn(
                      'leading-relaxed',
                      bodyTextClass,
                      !justifyBody && templateLayout === 'executive' && 'text-justify',
                    )}
                    style={ptStyle(bodySize)}
                  >
                    {field('summary', content.summary ?? '', {
                      multiline: true,
                      placeholder: 'Add via chat, or write your summary here.',
                      read: hl?.summary ? <WordSpans spans={hl.summary} /> : content.summary,
                    })}
                  </p>
                </section>
              ) : showEmptyState ? (
                <SectionHintStub
                  styles={styles}
                  title={templateLayout === 'executive' ? 'Professional Summary' : 'Summary'}
                  hint="Your professional summary will appear here as you chat."
                />
              ) : null

            const experienceSection =
              (content.experience && content.experience.length > 0) || builderEditable ? (
                <section className={styles.section}>
                  <h2 className={styles.sectionTitle} style={ptStyle(sectionHeaderSize)}>
                    {templateLayout === 'executive' ? 'Professional Experience' : 'Experience'}
                  </h2>
                  <div
                    className={cn(
                      'space-y-4',
                      templateLayout === 'pikachu' && 'divide-y divide-gray-200',
                    )}
                  >
                    {content.experience?.map((exp, expIdx) => (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: blank builder rows share empty title/company; append-only order
                        key={`exp-${expIdx}`}
                        className={cn(
                          templateLayout === 'pikachu' && 'pt-3 first:pt-0',
                          // timeline: connector line + dot marker down each entry -
                          // the archetype's one distinguishing visual, same idea as
                          // the backend's pure-CSS .experience-item::before dot.
                          template === 'timeline' &&
                            'relative border-l-2 border-[var(--resume-border)] pl-4 last:border-transparent',
                        )}
                      >
                        {template === 'timeline' && (
                          <span className="absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-[var(--resume-accent)]" />
                        )}
                        <div className="flex justify-between items-start">
                          {templateLayout === 'pikachu' ? (
                            // Pikachu: stacked job title and company
                            <div>
                              <div className={styles.jobTitle} style={ptStyle(subHeaderSize)}>
                                {field(`experience.${expIdx}.title`, exp.title, ph('Job title'))}
                              </div>
                              <div className={styles.company}>
                                {field(`experience.${expIdx}.company`, exp.company, ph('Company'))}
                              </div>
                            </div>
                          ) : templateLayout === 'executive' ? (
                            // Executive: comma-separated on one line
                            <div>
                              <span className={styles.jobTitle} style={ptStyle(subHeaderSize)}>
                                {field(`experience.${expIdx}.title`, exp.title, ph('Job title'))}
                              </span>
                              <span className="text-gray-500">
                                ,{' '}
                                {field(`experience.${expIdx}.company`, exp.company, ph('Company'))}
                              </span>
                            </div>
                          ) : (
                            // Modern: "at" separator with location
                            <div>
                              <span className={styles.jobTitle} style={ptStyle(subHeaderSize)}>
                                {field(`experience.${expIdx}.title`, exp.title, ph('Job title'))}
                              </span>
                              <span className="text-gray-500"> at </span>
                              <span className={styles.company}>
                                {field(`experience.${expIdx}.company`, exp.company, ph('Company'))}
                              </span>
                              {exp.location && (
                                <span className="text-sm text-gray-500 ml-1">({exp.location})</span>
                              )}
                            </div>
                          )}
                          <div className="flex items-start">
                            {templateLayout === 'pikachu' ? (
                              // Pikachu: stacked dates and location
                              <div className="text-right">
                                <div className={styles.dates}>
                                  {dateField(
                                    `experience.${expIdx}.start_date`,
                                    exp.start_date ?? '',
                                    ph('Start'),
                                  )}{' '}
                                  -{' '}
                                  {exp.end_date != null && exp.end_date !== ''
                                    ? dateField(`experience.${expIdx}.end_date`, exp.end_date, {
                                        allowPresent: true,
                                      })
                                    : builderEditable
                                      ? dateField(`experience.${expIdx}.end_date`, '', {
                                          ...ph('End / Present'),
                                          allowPresent: true,
                                        })
                                      : 'Present'}
                                </div>
                                {exp.location && (
                                  <div className="text-xs text-gray-500">{exp.location}</div>
                                )}
                              </div>
                            ) : (
                              // Modern/Executive: single line with location
                              <span className={styles.dates}>
                                {dateField(
                                  `experience.${expIdx}.start_date`,
                                  exp.start_date ?? '',
                                  ph('Start'),
                                )}{' '}
                                -{' '}
                                {exp.end_date != null && exp.end_date !== ''
                                  ? dateField(`experience.${expIdx}.end_date`, exp.end_date, {
                                      allowPresent: true,
                                    })
                                  : builderEditable
                                    ? dateField(`experience.${expIdx}.end_date`, '', {
                                        ...ph('End / Present'),
                                        allowPresent: true,
                                      })
                                    : 'Present'}
                                {exp.location &&
                                  templateLayout === 'executive' &&
                                  ` | ${exp.location}`}
                              </span>
                            )}
                            {removeButton('experience', expIdx)}
                          </div>
                        </div>
                        {exp.achievements && exp.achievements.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {exp.achievements.map((achievement, bulletIdx) => (
                              <li
                                // biome-ignore lint/suspicious/noArrayIndexKey: append-only bullets; blank rows share empty text
                                key={`exp-${expIdx}-ach-${bulletIdx}`}
                                className={cn(styles.bullet, justifyBody && 'text-justify')}
                              >
                                {field(
                                  `experience.${expIdx}.achievements.${bulletIdx}`,
                                  achievement,
                                  {
                                    multiline: true,
                                    ...ph('Describe what you did'),
                                    autoFocus:
                                      `experience.${expIdx}.achievements.${bulletIdx}` ===
                                      emptyBulletPath,
                                    read: hl?.achievements[expIdx]?.[bulletIdx] ? (
                                      <WordSpans
                                        spans={hl.achievements[expIdx]?.[bulletIdx] ?? []}
                                      />
                                    ) : (
                                      achievement
                                    ),
                                  },
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                        {builderEditable &&
                          onAddBullet &&
                          addRowButton('+ add a bullet', () => onAddBullet(expIdx))}
                      </div>
                    ))}
                  </div>
                  {builderEditable &&
                    onAddEntry &&
                    addRowButton('+ Add a role', () => onAddEntry('experience'))}
                </section>
              ) : showEmptyState ? (
                <SectionHintStub
                  styles={styles}
                  title={templateLayout === 'executive' ? 'Professional Experience' : 'Experience'}
                  hint="Tell me about a role in chat and I'll add it here."
                />
              ) : null

            const educationSection =
              (content.education && content.education.length > 0) || builderEditable ? (
                <section className={styles.section}>
                  <h2 className={styles.sectionTitle} style={ptStyle(sectionHeaderSize)}>
                    Education
                  </h2>
                  <div className="space-y-3">
                    {content.education?.map((edu, eduIdx) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: blank builder rows share empty degree/school; append-only order
                      <div key={`edu-${eduIdx}`} className="flex justify-between items-start">
                        <div>
                          <span className={styles.jobTitle} style={ptStyle(subHeaderSize)}>
                            {field(`education.${eduIdx}.degree`, edu.degree, ph('Degree'))}
                            {edu.field_of_study && (
                              <>
                                {' '}
                                in {field(`education.${eduIdx}.field_of_study`, edu.field_of_study)}
                              </>
                            )}
                          </span>
                          <div className={styles.company}>
                            {field(
                              `education.${eduIdx}.institution`,
                              edu.institution,
                              ph('School'),
                            )}
                          </div>
                          {edu.gpa && (
                            <span className="text-sm text-gray-500">
                              GPA: {field(`education.${eduIdx}.gpa`, edu.gpa)}
                            </span>
                          )}
                          {edu.honors && edu.honors.length > 0 && (
                            <div className="text-sm text-gray-600">{edu.honors.join(', ')}</div>
                          )}
                        </div>
                        <div className="flex items-start">
                          {/* A start date makes education read as a range, the way
                              experience does; education saved with a graduation
                              date alone still shows just that (PEL-1018). */}
                          {edu.start_date || edu.graduation_date || builderEditable ? (
                            <span className={styles.dates}>
                              {edu.start_date || builderEditable ? (
                                <>
                                  {dateField(
                                    `education.${eduIdx}.start_date`,
                                    edu.start_date ?? '',
                                    ph('Start'),
                                  )}{' '}
                                  -{' '}
                                </>
                              ) : null}
                              {edu.graduation_date
                                ? dateField(
                                    `education.${eduIdx}.graduation_date`,
                                    edu.graduation_date,
                                    { allowPresent: true },
                                  )
                                : builderEditable
                                  ? dateField(`education.${eduIdx}.graduation_date`, '', {
                                      ...ph(edu.start_date ? 'End / Present' : 'Year'),
                                      allowPresent: true,
                                    })
                                  : 'Present'}
                            </span>
                          ) : null}
                          {removeButton('education', eduIdx)}
                        </div>
                      </div>
                    ))}
                  </div>
                  {builderEditable &&
                    onAddEntry &&
                    addRowButton('+ Add education', () => onAddEntry('education'))}
                </section>
              ) : showEmptyState ? (
                <SectionHintStub
                  styles={styles}
                  title="Education"
                  hint="Tell me about your education in chat and I'll add it here."
                />
              ) : null

            const projectsSection = content.projects && content.projects.length > 0 && (
              <section className={styles.section}>
                <h2 className={styles.sectionTitle} style={ptStyle(sectionHeaderSize)}>
                  Projects
                </h2>
                <div className="space-y-3">
                  {content.projects.map((project, projIdx) => (
                    <div key={project.name}>
                      <div className="flex items-center gap-2">
                        <span className={styles.jobTitle} style={ptStyle(subHeaderSize)}>
                          {field(`projects.${projIdx}.name`, project.name)}
                        </span>
                        {project.url && (
                          <a
                            href={project.url}
                            className={cn('text-sm', styles.link)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Link
                          </a>
                        )}
                      </div>
                      {project.description && (
                        <p className={cn('text-sm mt-1', bodyTextClass)}>
                          {field(`projects.${projIdx}.description`, project.description, {
                            multiline: true,
                          })}
                        </p>
                      )}
                      {project.technologies && project.technologies.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {project.technologies.map((tech) => (
                            <span
                              key={tech}
                              className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )

            // PEL-1024: pikachu/two_column_photo are a real two-column
            // layout -- sidebar (contact/skills/certs/
            // languages) + main (header/summary/experience/education/
            // projects), matching DocumentRendererService's PDF builders
            // instead of faking these templates as a single top-down column.
            if (isSidebarLayout) {
              return (
                <div className="-m-8 flex">
                  {sidebarBlock}
                  <div className="min-w-0 flex-1 p-8">
                    {mainHeaderBlock}
                    {summarySection}
                    {experienceSection}
                    {educationSection}
                    {projectsSection}
                  </div>
                </div>
              )
            }

            return (
              <>
                {/* Header */}
                <div className={styles.header}>
                  {/* Modern: accent bar above name */}
                  {templateLayout === 'modern' && (
                    <div className="mb-3 h-1 w-12 rounded-full bg-[var(--resume-accent)]" />
                  )}
                  {nameField}
                  <div className={styles.contactInfo}>
                    {contactItems.map(([path, item], idx) => (
                      <span key={path} className="flex items-center">
                        {renderContactValue(path, item)}
                        {idx < contactItems.length - 1 && (
                          <span className="mx-2 text-gray-300">{contactSep}</span>
                        )}
                      </span>
                    ))}
                  </div>
                  {/* Modern: accent line below header */}
                  {templateLayout === 'modern' && (
                    <div className="mt-4 h-px bg-gradient-to-r from-[var(--resume-accent)] to-transparent" />
                  )}
                </div>

                {summarySection}
                {experienceSection}
                {educationSection}

                {/* Skills — existing chips plus an inline add-chip in the builder. */}
                {(content.skills && content.skills.length > 0) || builderEditable ? (
                  <section className={styles.section}>
                    <h2 className={styles.sectionTitle} style={ptStyle(sectionHeaderSize)}>
                      Skills
                    </h2>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {content.skills?.map((skill, skillIdx) => (
                        <span
                          key={skill}
                          className={cn(
                            'px-2.5 py-1 rounded-md text-xs font-medium',
                            hl?.skills[skillIdx] && !canEdit
                              ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-300'
                              : styles.skillBg,
                          )}
                        >
                          {field(`skills.${skillIdx}`, skill)}
                        </span>
                      ))}
                      {builderEditable && onAddEntry && (
                        <AddSkillChip styles={styles} onAdd={(v) => onAddEntry('skills', v)} />
                      )}
                    </div>
                    {builderEditable && !(content.skills && content.skills.length > 0) && (
                      <p className="mt-1 text-xs italic text-slate-400">
                        Add via chat, or type a skill above and press Enter.
                      </p>
                    )}
                  </section>
                ) : showEmptyState ? (
                  <SectionHintStub
                    styles={styles}
                    title="Skills"
                    hint="Your skills will appear here."
                  />
                ) : null}

                {/* Certifications */}
                {content.certifications && content.certifications.length > 0 && (
                  <section className={styles.section}>
                    <h2 className={styles.sectionTitle} style={ptStyle(sectionHeaderSize)}>
                      Certifications
                    </h2>
                    <ul className="space-y-1">
                      {content.certifications.map((cert, certIdx) => (
                        <li
                          key={cert}
                          className={cn(
                            bodyTextClass,
                            hl?.certifications[certIdx] &&
                              !canEdit &&
                              'bg-blue-50 ring-1 ring-blue-200 rounded-sm px-1',
                          )}
                        >
                          {field(`certifications.${certIdx}`, cert)}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {projectsSection}

                {/* Languages */}
                {content.languages && content.languages.length > 0 && (
                  <section className={styles.section}>
                    <h2 className={styles.sectionTitle} style={ptStyle(sectionHeaderSize)}>
                      Languages
                    </h2>
                    <p className={bodyTextClass}>{content.languages.join(', ')}</p>
                  </section>
                )}
              </>
            )
          })()
        )}
      </div>
    </div>
  )
}
